import React, { useState, useEffect, useCallback } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";

const statusConfig = {
  draft: { label: "Draft", color: "var(--color-muted-foreground)", bg: "rgba(148,163,184,0.12)" },
  sent: { label: "Sent", color: "var(--color-primary)", bg: "rgba(99,102,241,0.12)" },
  paid: { label: "Paid", color: "var(--color-success)", bg: "rgba(34,197,94,0.12)" },
  overdue: { label: "Overdue", color: "var(--color-error)", bg: "rgba(239,68,68,0.12)" },
  cancelled: { label: "Cancelled", color: "var(--color-warning)", bg: "rgba(234,179,8,0.12)" },
};

const billingCycleLabels = { monthly: "Monthly", yearly: "Yearly", one_time: "One-Time" };

const emptyForm = {
  vendor_id: "",
  plan_name: "",
  billing_cycle: "monthly",
  amount: "",
  tax_amount: "",
  billing_period_start: "",
  billing_period_end: "",
  due_date: "",
  invoice_status: "draft",
  notes: "",
};

function generateInvoiceNumber() {
  return "INV-" + Date.now()?.toString()?.slice(-6);
}

export default function VendorBilling() {
  const [activeSection, setActiveSection] = useState("invoices");
  const [invoices, setInvoices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterVendor, setFilterVendor] = useState("all");

  // Invoice form modal
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  // Renewal scheduling state
  const [renewalMap, setRenewalMap] = useState({});
  const [renewalSaving, setRenewalSaving] = useState({});

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [vendorRes, invoiceRes] = await Promise.all([
        api.get('/super-admin/vendors'),
        api.get('/super-admin/invoices'),
      ]);

      setVendors(vendorRes?.data || []);
      setInvoices(invoiceRes?.data || []);

      // Build renewal map from latest invoice per vendor
      const rMap = {};
      (invoiceRes?.data || [])?.forEach((inv) => {
        if (!rMap?.[inv?.vendor_id]) {
          rMap[inv?.vendor_id] = {
            next_renewal_date: inv?.next_renewal_date || "",
            auto_renew: inv?.auto_renew ?? true,
          };
        }
      });
      setRenewalMap(rMap);
    } catch (err) {
      console.error("Error fetching billing data:", err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Filtered invoices
  const filteredInvoices = invoices?.filter((inv) => {
    const vendorName = inv?.vendor_name || inv?.gym_name || "";
    const matchSearch =
      !search ||
      vendorName?.toLowerCase()?.includes(search?.toLowerCase()) ||
      inv?.invoice_number?.toLowerCase()?.includes(search?.toLowerCase()) ||
      inv?.plan_name?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = filterStatus === "all" || inv?.invoice_status === filterStatus;
    const matchVendor = filterVendor === "all" || inv?.vendor_id === filterVendor;
    return matchSearch && matchStatus && matchVendor;
  });

  // Summary stats
  const totalRevenue = invoices?.filter((i) => i?.invoice_status === "paid")?.reduce((s, i) => s + parseFloat(i?.total_amount || 0), 0) || 0;
  const pendingAmount = invoices?.filter((i) => ["sent", "draft"]?.includes(i?.invoice_status))?.reduce((s, i) => s + parseFloat(i?.total_amount || 0), 0) || 0;
  const overdueCount = invoices?.filter((i) => i?.invoice_status === "overdue")?.length || 0;
  const paidCount = invoices?.filter((i) => i?.invoice_status === "paid")?.length || 0;

  // Open create modal
  const openCreate = () => {
    setEditingInvoice(null);
    setForm({ ...emptyForm, invoice_number: generateInvoiceNumber() });
    setFormError(null);
    setShowInvoiceModal(true);
  };

  // Open edit modal
  const openEdit = (inv) => {
    setEditingInvoice(inv);
    setForm({
      vendor_id: inv?.vendor_id || "",
      plan_name: inv?.plan_name || "",
      billing_cycle: inv?.billing_cycle || "monthly",
      amount: inv?.amount || "",
      tax_amount: inv?.tax_amount || "",
      billing_period_start: inv?.billing_period_start || "",
      billing_period_end: inv?.billing_period_end || "",
      due_date: inv?.due_date || "",
      invoice_status: inv?.invoice_status || "draft",
      notes: inv?.notes || "",
    });
    setFormError(null);
    setShowInvoiceModal(true);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      // Auto-calculate total
      if (field === "amount" || field === "tax_amount") {
        const amt = parseFloat(field === "amount" ? value : prev?.amount) || 0;
        const tax = parseFloat(field === "tax_amount" ? value : prev?.tax_amount) || 0;
        updated.total_amount = (amt + tax)?.toFixed(2);
      }
      return updated;
    });
  };

  const handleSaveInvoice = async () => {
    if (!form?.vendor_id) { setFormError("Please select a vendor."); return; }
    if (!form?.amount || isNaN(parseFloat(form?.amount))) { setFormError("Please enter a valid amount."); return; }
    if (!form?.due_date) { setFormError("Please set a due date."); return; }

    setFormLoading(true);
    setFormError(null);
    try {
      const amt = parseFloat(form?.amount) || 0;
      const tax = parseFloat(form?.tax_amount) || 0;
      const payload = {
        vendor_id: form?.vendor_id,
        plan_name: form?.plan_name || null,
        billing_cycle: form?.billing_cycle,
        amount: amt,
        tax_amount: tax,
        total_amount: amt + tax,
        billing_period_start: form?.billing_period_start || null,
        billing_period_end: form?.billing_period_end || null,
        due_date: form?.due_date,
        invoice_status: form?.invoice_status,
        notes: form?.notes || null,
      };

      if (editingInvoice) {
        await api.patch(`/super-admin/invoices/${editingInvoice?.id}/status`, { invoice_status: payload?.invoice_status });
      } else {
        await api.post('/super-admin/invoices', payload);
      }

      setShowInvoiceModal(false);
      fetchAll();
    } catch (err) {
      setFormError(err?.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleMarkPaid = async (inv) => {
    try {
      await api.patch(`/super-admin/invoices/${inv?.id}/status`, { invoice_status: "paid", paid_at: new Date()?.toISOString() });
      fetchAll();
    } catch (err) {
      console.error("Error marking paid:", err);
    }
  };

  const handleDeleteInvoice = async (id) => {
    // No delete endpoint for invoices
    console.error("Invoice delete not supported by backend");
  };

  // Renewal scheduling save
  const handleSaveRenewal = async (vendorId) => {
    // No renewal endpoint
    console.error("Renewal scheduling not supported by backend");
  };

  const formatCurrency = (val) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })?.format(parseFloat(val) || 0);

  const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d)?.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const isOverdue = (inv) =>
    inv?.invoice_status !== "paid" &&
    inv?.invoice_status !== "cancelled" &&
    inv?.due_date &&
    new Date(inv?.due_date) < new Date();

  return (
    <div className="space-y-5">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Collected", value: formatCurrency(totalRevenue), icon: "DollarSign", color: "var(--color-success)" },
          { label: "Pending Amount", value: formatCurrency(pendingAmount), icon: "Clock", color: "var(--color-warning)" },
          { label: "Overdue Invoices", value: String(overdueCount), icon: "AlertTriangle", color: "var(--color-error)" },
          { label: "Paid Invoices", value: String(paidCount), icon: "CheckCircle", color: "var(--color-primary)" },
        ]?.map((card, i) => (
          <div key={i} className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)] p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${card?.color}18` }}>
              <Icon name={card?.icon} size={17} color={card?.color} />
            </div>
            <div>
              <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{card?.label}</p>
              <p className="text-lg font-bold font-[var(--font-data)] text-[var(--color-foreground)]">{card?.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Section Tabs */}
      <div className="flex items-center gap-1 border-b border-[var(--color-border)]">
        {[
          { id: "invoices", label: "Invoice List", icon: "FileText" },
          { id: "history", label: "Billing History", icon: "History" },
          { id: "renewal", label: "Renewal Scheduling", icon: "RefreshCw" },
        ]?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveSection(tab?.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium font-[var(--font-caption)] border-b-2 transition-colors -mb-px ${
              activeSection === tab?.id
                ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                : "border-transparent text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
            }`}
          >
            <Icon name={tab?.icon} size={13} color="currentColor" />
            {tab?.label}
          </button>
        ))}
      </div>

      {/* Invoice List & Billing History Sections */}
      {(activeSection === "invoices" || activeSection === "history") && (
        <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)]">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 border-b border-[var(--color-border)]">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Icon name="Search" size={13} color="var(--color-muted-foreground)" className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search invoices..."
                  value={search}
                  onChange={(e) => setSearch(e?.target?.value)}
                  className="pl-8 pr-3 py-1.5 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-background)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] w-44"
                />
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e?.target?.value)}
                className="px-2.5 py-1.5 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-background)] text-[var(--color-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
              >
                <option value="all">All Status</option>
                {Object.entries(statusConfig)?.map(([k, v]) => (
                  <option key={k} value={k}>{v?.label}</option>
                ))}
              </select>
              <select
                value={filterVendor}
                onChange={(e) => setFilterVendor(e?.target?.value)}
                className="px-2.5 py-1.5 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-background)] text-[var(--color-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
              >
                <option value="all">All Vendors</option>
                {vendors?.map((v) => (
                  <option key={v?.id} value={v?.id}>{v?.full_name || v?.gym_name}</option>
                ))}
              </select>
            </div>
            {activeSection === "invoices" && (
              <button
                onClick={openCreate}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors flex-shrink-0"
              >
                <Icon name="Plus" size={13} color="white" />
                Generate Invoice
              </button>
            )}
          </div>

          {/* Table */}
          {loading ? (
            <div className="p-8 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="p-6 text-center text-sm text-[var(--color-error)]">{error}</div>
          ) : filteredInvoices?.length === 0 ? (
            <div className="p-10 text-center">
              <Icon name="FileText" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-40" />
              <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">No invoices found</p>
              {activeSection === "invoices" && (
                <button onClick={openCreate} className="mt-3 text-xs text-[var(--color-primary)] hover:underline">Generate your first invoice</button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[var(--color-border)] bg-[var(--color-muted)]/30">
                    {["Invoice #", "Vendor", "Plan", "Cycle", "Amount", "Due Date", "Paid At", "Status", "Actions"]?.map((h) => (
                      <th key={h} className="text-left px-4 py-2.5 font-semibold text-[var(--color-muted-foreground)] font-[var(--font-caption)] whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices?.map((inv) => {
                    const st = statusConfig?.[inv?.invoice_status] || statusConfig?.draft;
                    const overdue = isOverdue(inv);
                    return (
                      <tr key={inv?.id} className={`border-b border-[var(--color-border)] hover:bg-[var(--color-muted)]/20 transition-colors ${overdue ? "bg-[var(--color-error)]/3" : ""}`}>
                        <td className="px-4 py-3 font-mono font-medium text-[var(--color-foreground)]">{inv?.invoice_number}</td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-[var(--color-foreground)]">{inv?.vendor_name || "—"}</p>
                            <p className="text-[var(--color-muted-foreground)] text-[10px]">{inv?.gym_name || inv?.vendor_email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[var(--color-foreground)]">{inv?.plan_name || "—"}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[var(--color-muted)] text-[var(--color-muted-foreground)]">
                            {billingCycleLabels?.[inv?.billing_cycle] || inv?.billing_cycle}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-[var(--font-data)] font-semibold text-[var(--color-foreground)]">
                          {formatCurrency(inv?.total_amount)}
                        </td>
                        <td className={`px-4 py-3 ${overdue ? "text-[var(--color-error)] font-semibold" : "text-[var(--color-foreground)]"}`}>
                          {formatDate(inv?.due_date)}
                          {overdue && <span className="ml-1 text-[10px]">⚠</span>}
                        </td>
                        <td className="px-4 py-3 text-[var(--color-muted-foreground)]">{formatDate(inv?.paid_at)}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ color: st?.color, backgroundColor: st?.bg }}>
                            {st?.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {activeSection === "invoices" && (
                              <>
                                <button
                                  onClick={() => openEdit(inv)}
                                  title="Edit"
                                  className="p-1.5 rounded hover:bg-[var(--color-muted)] transition-colors text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
                                >
                                  <Icon name="Pencil" size={12} color="currentColor" />
                                </button>
                                {inv?.invoice_status !== "paid" && (
                                  <button
                                    onClick={() => handleMarkPaid(inv)}
                                    title="Mark as Paid"
                                    className="p-1.5 rounded hover:bg-[var(--color-success)]/10 transition-colors text-[var(--color-success)]"
                                  >
                                    <Icon name="CheckCircle" size={12} color="currentColor" />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteInvoice(inv?.id)}
                                  title="Delete"
                                  className="p-1.5 rounded hover:bg-[var(--color-error)]/10 transition-colors text-[var(--color-error)]"
                                >
                                  <Icon name="Trash2" size={12} color="currentColor" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer count */}
          {!loading && filteredInvoices?.length > 0 && (
            <div className="px-4 py-2.5 border-t border-[var(--color-border)] text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
              Showing {filteredInvoices?.length} of {invoices?.length} invoice{invoices?.length !== 1 ? "s" : ""}
            </div>
          )}
        </div>
      )}

      {/* Renewal Scheduling Section */}
      {activeSection === "renewal" && (
        <div className="space-y-4">
          <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)] p-4">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="RefreshCw" size={15} color="var(--color-primary)" />
              <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">Subscription Renewal Scheduling</h3>
            </div>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-5">
              Set the next renewal date and configure auto-renew settings for each vendor subscription.
            </p>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : vendors?.length === 0 ? (
              <p className="text-sm text-[var(--color-muted-foreground)] text-center py-6">No vendors found.</p>
            ) : (
              <div className="space-y-3">
                {vendors?.map((vendor) => {
                  const renewal = renewalMap?.[vendor?.id] || { next_renewal_date: "", auto_renew: true };
                  const vendorInvoices = invoices?.filter((i) => i?.vendor_id === vendor?.id);
                  const latestInvoice = vendorInvoices?.[0];
                  const saving = renewalSaving?.[vendor?.id];

                  return (
                    <div key={vendor?.id} className="border border-[var(--color-border)] rounded-[var(--radius-sm)] p-4 hover:bg-[var(--color-muted)]/20 transition-colors">
                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        {/* Vendor Info */}
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-9 h-9 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center flex-shrink-0">
                            <Icon name="Building2" size={16} color="var(--color-primary)" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-heading)] truncate">{vendor?.full_name}</p>
                            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] truncate">{vendor?.gym_name || vendor?.email}</p>
                            {latestInvoice && (
                              <p className="text-[10px] text-[var(--color-muted-foreground)] mt-0.5">
                                Plan: <span className="font-medium">{latestInvoice?.plan_name || "—"}</span>
                                {" · "}
                                {billingCycleLabels?.[latestInvoice?.billing_cycle] || ""}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Renewal Controls */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                          <div>
                            <label className="block text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1">Next Renewal Date</label>
                            <input
                              type="date"
                              value={renewal?.next_renewal_date || ""}
                              onChange={(e) =>
                                setRenewalMap((prev) => ({
                                  ...prev,
                                  [vendor?.id]: { ...prev?.[vendor?.id], next_renewal_date: e?.target?.value },
                                }))
                              }
                              className="px-2.5 py-1.5 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-background)] text-[var(--color-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                            />
                          </div>

                          <div className="flex flex-col items-start gap-1">
                            <label className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Auto-Renew</label>
                            <button
                              onClick={() =>
                                setRenewalMap((prev) => ({
                                  ...prev,
                                  [vendor?.id]: { ...prev?.[vendor?.id], auto_renew: !prev?.[vendor?.id]?.auto_renew },
                                }))
                              }
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                                renewal?.auto_renew ? "bg-[var(--color-primary)]" : "bg-[var(--color-muted)]"
                              }`}
                            >
                              <span
                                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                                  renewal?.auto_renew ? "translate-x-4.5" : "translate-x-0.5"
                                }`}
                              />
                            </button>
                          </div>

                          <button
                            onClick={() => handleSaveRenewal(vendor?.id)}
                            disabled={saving || !renewal?.next_renewal_date}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-3 sm:mt-0 self-end sm:self-auto"
                          >
                            {saving ? (
                              <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Icon name="Save" size={12} color="white" />
                            )}
                            Save
                          </button>
                        </div>

                        {/* Renewal Status Badge */}
                        {renewal?.next_renewal_date && (
                          <div className="flex-shrink-0">
                            {new Date(renewal?.next_renewal_date) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) ? (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[var(--color-warning)]/12 text-[var(--color-warning)]">
                                Renews Soon
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[var(--color-success)]/12 text-[var(--color-success)]">
                                {formatDate(renewal?.next_renewal_date)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Invoice Create/Edit Modal */}
      {showInvoiceModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !formLoading && setShowInvoiceModal(false)} />
          <div className="relative bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] shadow-[var(--shadow-2xl)] w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--color-border)] sticky top-0 bg-[var(--color-card)] z-10">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md bg-[var(--color-primary)]/10 flex items-center justify-center">
                  <Icon name="FileText" size={14} color="var(--color-primary)" />
                </div>
                <h2 className="text-sm font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">
                  {editingInvoice ? "Edit Invoice" : "Generate Invoice"}
                </h2>
              </div>
              <button
                onClick={() => !formLoading && setShowInvoiceModal(false)}
                className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors text-[var(--color-muted-foreground)]"
              >
                <Icon name="X" size={15} color="currentColor" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4">
              {formError && (
                <div className="flex items-center gap-2 p-3 rounded-md bg-[var(--color-error)]/10 border border-[var(--color-error)]/20">
                  <Icon name="AlertCircle" size={13} color="var(--color-error)" />
                  <p className="text-xs text-[var(--color-error)] font-[var(--font-caption)]">{formError}</p>
                </div>
              )}

              {/* Invoice Number (read-only for new) */}
              {!editingInvoice && (
                <div>
                  <label className="block text-xs font-medium text-[var(--color-foreground)] font-[var(--font-caption)] mb-1">Invoice Number</label>
                  <input
                    type="text"
                    value={form?.invoice_number || ""}
                    readOnly
                    className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-muted)]/40 text-[var(--color-muted-foreground)] font-mono"
                  />
                </div>
              )}

              {/* Vendor */}
              <div>
                <label className="block text-xs font-medium text-[var(--color-foreground)] font-[var(--font-caption)] mb-1">Vendor <span className="text-[var(--color-error)]">*</span></label>
                <select
                  value={form?.vendor_id}
                  onChange={(e) => handleFormChange("vendor_id", e?.target?.value)}
                  className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-background)] text-[var(--color-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                >
                  <option value="">Select vendor...</option>
                  {vendors?.map((v) => (
                    <option key={v?.id} value={v?.id}>{v?.full_name} {v?.gym_name ? `(${v?.gym_name})` : ""}</option>
                  ))}
                </select>
              </div>

              {/* Plan Name & Billing Cycle */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[var(--color-foreground)] font-[var(--font-caption)] mb-1">Plan Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Pro Plan"
                    value={form?.plan_name}
                    onChange={(e) => handleFormChange("plan_name", e?.target?.value)}
                    className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-background)] text-[var(--color-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--color-foreground)] font-[var(--font-caption)] mb-1">Billing Cycle</label>
                  <select
                    value={form?.billing_cycle}
                    onChange={(e) => handleFormChange("billing_cycle", e?.target?.value)}
                    className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-background)] text-[var(--color-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                    <option value="one_time">One-Time</option>
                  </select>
                </div>
              </div>

              {/* Amount & Tax */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[var(--color-foreground)] font-[var(--font-caption)] mb-1">Amount ($) <span className="text-[var(--color-error)]">*</span></label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={form?.amount}
                    onChange={(e) => handleFormChange("amount", e?.target?.value)}
                    className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-background)] text-[var(--color-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--color-foreground)] font-[var(--font-caption)] mb-1">Tax ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={form?.tax_amount}
                    onChange={(e) => handleFormChange("tax_amount", e?.target?.value)}
                    className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-background)] text-[var(--color-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                  />
                </div>
              </div>

              {/* Total (computed) */}
              {(form?.amount || form?.tax_amount) && (
                <div className="flex items-center justify-between px-3 py-2 rounded-md bg-[var(--color-primary)]/5 border border-[var(--color-primary)]/15">
                  <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Total Amount</span>
                  <span className="text-sm font-bold font-[var(--font-data)] text-[var(--color-primary)]">
                    {formatCurrency((parseFloat(form?.amount) || 0) + (parseFloat(form?.tax_amount) || 0))}
                  </span>
                </div>
              )}

              {/* Billing Period */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[var(--color-foreground)] font-[var(--font-caption)] mb-1">Period Start</label>
                  <input
                    type="date"
                    value={form?.billing_period_start}
                    onChange={(e) => handleFormChange("billing_period_start", e?.target?.value)}
                    className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-background)] text-[var(--color-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--color-foreground)] font-[var(--font-caption)] mb-1">Period End</label>
                  <input
                    type="date"
                    value={form?.billing_period_end}
                    onChange={(e) => handleFormChange("billing_period_end", e?.target?.value)}
                    className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-background)] text-[var(--color-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                  />
                </div>
              </div>

              {/* Due Date & Status */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[var(--color-foreground)] font-[var(--font-caption)] mb-1">Due Date <span className="text-[var(--color-error)]">*</span></label>
                  <input
                    type="date"
                    value={form?.due_date}
                    onChange={(e) => handleFormChange("due_date", e?.target?.value)}
                    className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-background)] text-[var(--color-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--color-foreground)] font-[var(--font-caption)] mb-1">Status</label>
                  <select
                    value={form?.invoice_status}
                    onChange={(e) => handleFormChange("invoice_status", e?.target?.value)}
                    className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-background)] text-[var(--color-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                  >
                    {Object.entries(statusConfig)?.map(([k, v]) => (
                      <option key={k} value={k}>{v?.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-[var(--color-foreground)] font-[var(--font-caption)] mb-1">Notes</label>
                <textarea
                  rows={2}
                  placeholder="Optional notes..."
                  value={form?.notes}
                  onChange={(e) => handleFormChange("notes", e?.target?.value)}
                  className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-background)] text-[var(--color-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] resize-none"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[var(--color-border)] sticky bottom-0 bg-[var(--color-card)]">
              <button
                onClick={() => !formLoading && setShowInvoiceModal(false)}
                disabled={formLoading}
                className="px-4 py-2 text-xs font-medium border border-[var(--color-border)] rounded-md text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveInvoice}
                disabled={formLoading}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-60"
              >
                {formLoading ? (
                  <><div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />Saving...</>
                ) : (
                  <><Icon name="Save" size={12} color="white" />{editingInvoice ? "Update Invoice" : "Generate Invoice"}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
